import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import Images from "../../../utils/Images";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff, Check, X } from "lucide-react";
import authService from "../../../Services/AuthService";
import { toast } from "react-toastify";

export default function ResetPassword() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [code, setCode] = useState(["", "", "", ""]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: [],
  });
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!state?.method || !state?.contact || !state?.slug) {
      // If accessed directly, bounce back to request screen
      navigate("/forgot-password", { replace: true });
    } else {
      // Auto-focus the first OTP input
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    }
  }, [state, navigate]);

  // Password strength calculation
  const calculatePasswordStrength = (pwd) => {
    const feedback = [];
    let score = 0;

    if (pwd.length >= 8) {
      score += 1;
      feedback.push({ text: "At least 8 characters", valid: true });
    } else {
      feedback.push({ text: "At least 8 characters", valid: false });
    }

    if (/[A-Z]/.test(pwd)) {
      score += 1;
      feedback.push({ text: "One uppercase letter", valid: true });
    } else {
      feedback.push({ text: "One uppercase letter", valid: false });
    }

    if (/[a-z]/.test(pwd)) {
      score += 1;
      feedback.push({ text: "One lowercase letter", valid: true });
    } else {
      feedback.push({ text: "One lowercase letter", valid: false });
    }

    if (/[0-9]/.test(pwd)) {
      score += 1;
      feedback.push({ text: "One number", valid: true });
    } else {
      feedback.push({ text: "One number", valid: false });
    }

    if (/[^A-Za-z0-9]/.test(pwd)) {
      score += 1;
      feedback.push({ text: "One special character", valid: true });
    } else {
      feedback.push({ text: "One special character", valid: false });
    }

    return { score, feedback };
  };

  // Handle password change
  const handlePasswordChange = (value) => {
    setPassword(value);
    setError("");
    setPasswordStrength(calculatePasswordStrength(value));
  };

  const handleInputChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const next = [...code];
      next[index] = value;
      setCode(next);
      if (value && index < 3) inputRefs.current[index + 1]?.focus();
    }
  };
  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0)
      inputRefs.current[index - 1]?.focus();
  };

  const isCodeComplete = code.every((d) => d !== "");
  const isPasswordStrong = passwordStrength.score >= 4; // Require at least 4/5 criteria
  const passwordsMatch = password === confirmPassword;
  const canSubmit =
    isCodeComplete &&
    password &&
    confirmPassword &&
    passwordsMatch &&
    isPasswordStrong;

  const submit = async (e) => {
    if (e) e.preventDefault(); // Prevent default form submission
    if (!canSubmit || loading) return;

    setLoading(true);
    setError("");

    try {
      const payload = {
        password: password,
        otp: parseInt(code.join("")),
        slug: state.slug,
      };

      const response = await authService.resetPassword(payload);

      if (response.status) {
        // Password reset successful, show success toast and redirect to login
        toast.success(
          "Password reset successfully! Please login with your new password.",
          {
            position: "top-center",
            autoClose: 3000,
          }
        );
        setTimeout(() => {
          navigate("/signin");
        }, 1500);
      } else {
        const errorMessage = response.message || "Password reset failed";
        setError(errorMessage);
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 4000,
        });
      }
    } catch (error) {
      const errorMessage = "An unexpected error occurred. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative h-screen overflow-hidden">
      <img
        src="https://images.unsplash.com/photo-1557804506-669a67965ba0?q=80&w=1600&auto=format&fit=crop"
        alt="bg"
        className="object-cover absolute inset-0 w-full h-full"
      />
      <div className="absolute inset-0 bg-black/60 pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="flex absolute top-0 left-0 right-0 z-10 justify-center pt-6"
      >
        <img src={Images.logo_navbar} alt="TripSecure" className="h-10" />
      </motion.div>
      <button
        onClick={() => navigate(-1)}
        className="flex absolute top-4 left-4 z-20 items-center gap-2 px-3 py-2 text-xs font-medium text-white/90 rounded-lg border border-white/20 bg-white/10 backdrop-blur hover:bg-white/20"
      >
        <ArrowLeft size={14} /> Back
      </button>

      <div className="flex relative z-10 justify-center items-center h-screen p-6">
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="p-6 w-full max-w-md rounded-3xl shadow-2xl bg-white/95"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            <h2 className="text-lg font-semibold text-center">
              Create New Password
            </h2>
            <p className="mt-1 text-xs text-center text-gray-500">
              Enter the code you received and your new password
            </p>
          </motion.div>

          <form onSubmit={submit} className="mt-6 space-y-5">
            <div className="flex gap-3 justify-center">
              {code.map((digit, index) => (
                <div key={index} className="relative group">
                  <div className="relative rounded-full p-[2px] bg-gradient-to-br from-emerald-400/40 via-transparent to-emerald-400/40 group-focus-within:from-emerald-500/60 group-focus-within:to-emerald-500/60">
                    <input
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      value={digit}
                      onChange={(e) => handleInputChange(index, e.target.value)}
                      onKeyDown={(e) => {
                        handleKeyDown(index, e);
                        // Submit form when Enter is pressed on the last OTP input
                        if (
                          e.key === "Enter" &&
                          index === 3 &&
                          isCodeComplete
                        ) {
                          submit(e);
                        }
                      }}
                      disabled={loading}
                      className={`w-14 h-14 text-lg font-bold text-center rounded-full border-0 outline-none bg-white/95 focus:text-emerald-700 ${
                        loading ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      maxLength={1}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-3">
              {/* Password Input */}
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="New password"
                  value={password}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      submit(e);
                    }
                  }}
                  disabled={loading}
                  className={`px-4 py-3 pr-12 w-full text-sm rounded-xl border border-gray-200 outline-none bg-white/95 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 ${
                    loading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {/* Password Strength Indicator */}
              {password && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          passwordStrength.score <= 1
                            ? "bg-red-500"
                            : passwordStrength.score <= 2
                            ? "bg-orange-500"
                            : passwordStrength.score <= 3
                            ? "bg-yellow-500"
                            : passwordStrength.score <= 4
                            ? "bg-blue-500"
                            : "bg-green-500"
                        }`}
                        style={{
                          width: `${(passwordStrength.score / 5) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-600">
                      {passwordStrength.score <= 1
                        ? "Weak"
                        : passwordStrength.score <= 2
                        ? "Fair"
                        : passwordStrength.score <= 3
                        ? "Good"
                        : passwordStrength.score <= 4
                        ? "Strong"
                        : "Very Strong"}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {passwordStrength.feedback.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 text-xs"
                      >
                        {item.valid ? (
                          <Check size={12} className="text-green-500" />
                        ) : (
                          <X size={12} className="text-red-500" />
                        )}
                        <span
                          className={
                            item.valid ? "text-green-600" : "text-red-600"
                          }
                        >
                          {item.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Confirm Password Input */}
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setError(""); // Clear error when user types
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      submit(e);
                    }
                  }}
                  disabled={loading}
                  className={`px-4 py-3 pr-12 w-full text-sm rounded-xl border outline-none bg-white/95 focus:ring-2 focus:ring-emerald-100 ${
                    confirmPassword && passwordsMatch
                      ? "border-green-500 focus:border-green-600"
                      : confirmPassword && !passwordsMatch
                      ? "border-red-500 focus:border-red-600"
                      : "border-gray-200 focus:border-emerald-600"
                  } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={loading}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed"
                >
                  {showConfirmPassword ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              </div>

              {/* Password Match Indicator */}
              {confirmPassword && (
                <div className="flex items-center gap-2 text-xs">
                  {passwordsMatch ? (
                    <>
                      <Check size={12} className="text-green-500" />
                      <span className="text-green-600">Passwords match</span>
                    </>
                  ) : (
                    <>
                      <X size={12} className="text-red-500" />
                      <span className="text-red-600">
                        Passwords do not match
                      </span>
                    </>
                  )}
                </div>
              )}

              {error && <p className="text-xs text-red-500">{error}</p>}
            </div>

            <motion.button
              type="submit"
              disabled={!canSubmit || loading}
              whileHover={{ scale: canSubmit && !loading ? 1.02 : 1 }}
              whileTap={{ scale: canSubmit && !loading ? 0.98 : 1 }}
              className="overflow-hidden relative px-4 py-3 w-full text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl shadow-lg hover:from-emerald-600 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-400/50 focus:ring-offset-2 disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Resetting...
                </div>
              ) : (
                "Reset Password"
              )}
            </motion.button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
